import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Move } from 'lucide-react';
import TimerPlaybackControls from './TimerPlaybackControls';

export default function TimerStageRenderer({
  timer,
  timerState, // { status: 'idle'|'running'|'paused', serverStartTime, elapsed, remaining }
  onStateUpdate,
  onTimerEnd,
  isHost = true,
  meetingAreaRef,
}) {
  const [displayTime, setDisplayTime] = useState(0);
  const [isFlashing, setIsFlashing] = useState(false);
  const animationRef = useRef(null);

  const timerType = timer?.timer_type;
  const config = timer?.config || timer || {};
  const [currentPosition, setCurrentPosition] = useState(config.position || timer?.position || 'bottom_right');

  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const calculateDisplayTime = useCallback(() => {
    if (!timer || !timerState) return 0;

    const now = Date.now() / 1000;

    if (timerState.status === 'idle') {
      if (timerType === 'countdown_duration') {
        return timerState.durationOverride || config.durationSeconds || 0;
      }
      if (timerType === 'countdown_datetime') {
        return Math.max(0, (config.targetTimestamp || 0) - now);
      }
      return 0;
    }

    if (timerState.status === 'paused') {
      if (timerType === 'countup') {
        return timerState.elapsed || 0;
      }
      return timerState.remaining || 0;
    }

    if (timerState.status === 'running') {
      const elapsed = now - (timerState.serverStartTime || now);

      if (timerType === 'countup') {
        return (timerState.baseElapsed || 0) + elapsed;
      }

      if (timerType === 'countdown_duration') {
        const baseRemaining = timerState.baseRemaining ?? config.durationSeconds;
        return Math.max(0, baseRemaining - elapsed);
      }

      if (timerType === 'countdown_datetime') {
        return Math.max(0, (config.targetTimestamp || 0) - now);
      }
    }

    return 0;
  }, [timer, timerState, timerType, config]);

  useEffect(() => {
    if (!timer) return;

    const updateDisplay = () => {
      const newTime = calculateDisplayTime();
      setDisplayTime(newTime);

      if (timerType !== 'countup' && timerState?.status === 'running') {
        if (newTime <= 0) {
          handleTimerReachZero();
          return;
        }
      }

      animationRef.current = requestAnimationFrame(updateDisplay);
    };

    if (timerState?.status === 'running') {
      animationRef.current = requestAnimationFrame(updateDisplay);
    } else {
      setDisplayTime(calculateDisplayTime());
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [timer, timerState, calculateDisplayTime, timerType]);

  const handleTimerReachZero = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    setDisplayTime(0);

    if (config.loopOnZero && timerType === 'countdown_duration') {
      onStateUpdate?.({
        status: 'running',
        serverStartTime: Date.now() / 1000,
        baseRemaining: config.durationSeconds
      });
      return;
    }

    switch (config.actionOnZero) {
      case 'hide':
        onTimerEnd?.();
        break;
      case 'flash':
        setIsFlashing(true);
        setTimeout(() => {
          setIsFlashing(false);
          onStateUpdate?.({ status: 'idle' });
        }, 4000);
        break;
      case 'stop':
      default:
        onStateUpdate?.({ status: 'idle' });
        break;
    }
  }, [config, timerType, onStateUpdate, onTimerEnd]);

  const handleStart = useCallback(() => {
    const now = Date.now() / 1000;

    if (timerType === 'countup') {
      onStateUpdate?.({
        status: 'running',
        serverStartTime: now,
        baseElapsed: timerState?.elapsed || 0
      });
    } else {
      const baseRemaining = timerState?.remaining ?? timerState?.durationOverride ??
        (timerType === 'countdown_duration' ? config.durationSeconds :
         Math.max(0, (config.targetTimestamp || 0) - now));

      onStateUpdate?.({
        status: 'running',
        serverStartTime: now,
        baseRemaining
      });
    }
  }, [timerType, timerState, config, onStateUpdate]);

  const handlePause = useCallback(() => {
    const currentTime = calculateDisplayTime();

    if (timerType === 'countup') {
      onStateUpdate?.({
        status: 'paused',
        elapsed: currentTime
      });
    } else {
      onStateUpdate?.({
        status: 'paused',
        remaining: currentTime
      });
    }
  }, [timerType, calculateDisplayTime, onStateUpdate]);

  const handleReset = useCallback(() => {
    if (timerType === 'countup') {
      onStateUpdate?.({
        status: 'idle',
        elapsed: 0,
        baseElapsed: 0
      });
    } else if (timerType === 'countdown_duration') {
      onStateUpdate?.({
        status: 'idle',
        remaining: config.durationSeconds,
        baseRemaining: config.durationSeconds
      });
    } else {
      onStateUpdate?.({ status: 'idle' });
    }
  }, [timerType, config, onStateUpdate]);

  const handleAddTime = useCallback((seconds) => {
    if (timerType !== 'countdown_duration') return;

    if (timerState?.status === 'running') {
      const newBaseRemaining = (timerState.baseRemaining ?? config.durationSeconds) + seconds;
      if (newBaseRemaining <= 0) return;
      onStateUpdate?.({
        ...timerState,
        baseRemaining: newBaseRemaining
      });
    } else if (timerState?.status === 'paused') {
      const newRemaining = (timerState.remaining || 0) + seconds;
      if (newRemaining <= 0) return;
      onStateUpdate?.({
        ...timerState,
        remaining: newRemaining
      });
    } else if (timerState?.status === 'idle' || !timerState?.status) {
      // Idle: adjust the base duration (use current override if already adjusted)
      const currentDuration = timerState?.durationOverride || config.durationSeconds || 0;
      const newDuration = currentDuration + seconds;
      if (newDuration <= 0) return;
      onStateUpdate?.({
        status: 'idle',
        remaining: newDuration,
        baseRemaining: newDuration,
        durationOverride: newDuration
      });
    }
  }, [timerType, timerState, config, onStateUpdate]);

  const handleHide = useCallback(() => {
    onTimerEnd?.();
  }, [onTimerEnd]);

  useEffect(() => {
    const pos = config.position || timer?.position || 'bottom_right';
    setCurrentPosition(pos);
  }, [timer?.id, config.position, timer?.position]);

  useEffect(() => {
    if (timer && config.autoStartOnShow && timerState?.status === 'idle') {
      handleStart();
    }
  }, [timer?.id]);

  const formatTime = (seconds) => {
    if (seconds === undefined || seconds === null || isNaN(seconds)) seconds = 0;
    if (seconds < 0) seconds = 0;

    const format = config.format || 'MM_SS';
    const totalSeconds = Math.floor(seconds);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (format === 'D_HH_MM_SS' && days > 0) {
      return `${days}d ${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }

    if (format === 'HH_MM_SS' || hours > 0 || config.showHours) {
      return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }

    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  if (!timer) return null;

  const getPositionStyles = () => {
    // Bottom positions need extra offset to stay above the bottom controls bar
    // Mobile bottom bar is ~80px (h-20), add extra padding
    const bottomOffset = window.innerWidth < 640 ? 90 : 80;
    const edgeOffset = window.innerWidth < 640 ? 8 : 16;
    switch (currentPosition) {
      case 'top_left': return { top: edgeOffset, left: edgeOffset };
      case 'top_right': return { top: edgeOffset, right: edgeOffset };
      case 'bottom_left': return { bottom: bottomOffset, left: edgeOffset };
      case 'bottom_right': return { bottom: bottomOffset, right: edgeOffset };
      case 'center': return { top: '50%', left: '50%', x: '-50%', y: '-50%' };
      default: return { bottom: bottomOffset, right: edgeOffset };
    }
  };

  const handlePositionChange = (newPosition) => {
    setCurrentPosition(newPosition);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const getSizeClasses = () => {
    switch (config.size) {
      case 'small': return 'text-xl sm:text-2xl px-2.5 sm:px-3 py-1 sm:py-1.5';
      case 'large': return 'text-3xl sm:text-6xl px-4 sm:px-8 py-2.5 sm:py-5';
      case 'medium':
      default: return 'text-2xl sm:text-4xl px-3 sm:px-5 py-2 sm:py-3';
    }
  };

  const theme = config.theme || {};
  const positionStyles = getPositionStyles();

  return (
    <>
      <AnimatePresence>
        <motion.div
          ref={containerRef}
          drag
          dragMomentum={false}
          dragConstraints={meetingAreaRef || { left: -500, right: 500, top: -300, bottom: 300 }}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={handleDragEnd}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{
            opacity: 1,
            scale: isFlashing ? [1, 1.05, 1] : 1,
          }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{
            duration: 0.3,
            scale: isFlashing ? { repeat: Infinity, duration: 0.5 } : {}
          }}
          className="absolute z-50 cursor-grab active:cursor-grabbing"
          style={positionStyles}
        >
          <div className="relative group">
            <div className={`absolute -top-6 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2 py-0.5 rounded bg-black/60 text-white text-[10px] transition-opacity ${isDragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
              <Move className="w-3 h-3" />
              <span>Drag to move</span>
            </div>
            <div
              className={`rounded-xl font-mono font-bold ${getSizeClasses()} ${
                isFlashing ? 'animate-pulse' : ''
              } ring-2 ring-transparent group-hover:ring-white/30 transition-all`}
              style={{
                backgroundColor: theme.transparentBackground ? 'transparent' : (theme.backgroundColor || '#6a1fbf'),
                color: theme.textColor || '#ffffff',
                border: theme.transparentBackground ? 'none' : `2px solid ${theme.borderColor || theme.backgroundColor || '#6a1fbf'}`,
                textShadow: theme.transparentBackground ? '0 2px 12px rgba(0,0,0,0.9)' : 'none',
                boxShadow: theme.transparentBackground ? 'none' : '0 4px 20px rgba(0,0,0,0.4)'
              }}
            >
              {config.showLabel && config.labelText && (
                <div
                  className="text-sm font-normal mb-1 opacity-80"
                  style={{ color: theme.textColor || '#ffffff' }}
                >
                  {config.labelText}
                </div>
              )}
              {formatTime(displayTime)}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {isHost && (
        <TimerPlaybackControls
          timer={timer}
          status={timerState?.status || 'idle'}
          displayTime={formatTime(displayTime)}
          onStart={handleStart}
          onPause={handlePause}
          onReset={handleReset}
          onHide={handleHide}
          onAddTime={handleAddTime}
          currentPosition={currentPosition}
          onPositionChange={handlePositionChange}
        />
      )}
    </>
  );
}
